import { useMemo, useState } from "react";
import { Trophy } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { money, calcCommission, effectiveRate, calcAgentCommission } from "../lib/finance";
import { CompanyLogo } from "../lib/companies";
import { Empty } from "./common";
import { CompanyDetailSheet } from "./CompanyDetailSheet";

const SORTS = [["personal", "Komisyon"], ["prim", "Prim"], ["count", "Poliçe"]];
const NONE = "(Belirtilmemiş)";

function useCompanyStats(policies, customers, branches, settings, year) {
  return useMemo(() => {
    const map = {};
    policies.forEach((p) => {
      if (!p.start || new Date(p.start).getFullYear() !== year) return;
      const key = (p.company || "").trim() || NONE;
      const r = (map[key] ||= { company: key, count: 0, prim: 0, personal: 0, agent: 0 });
      r.count += 1;
      r.prim += Number(p.premium || 0);
      r.personal += calcCommission(p.premium, effectiveRate(p, branches), settings.netDivisor, settings.personalShare, p.netPremium, p.status === "iptal" ? p.cancellationRefund : null).personal;
      r.agent += calcAgentCommission(p, customers, branches, settings).agent;
    });
    return Object.values(map);
  }, [policies, customers, branches, settings, year]);
}

function Summary({ rows, totals }) {
  const top = [...rows].sort((a, b) => b.personal - a.personal)[0];
  return (
    <div className="company-summary" data-testid="company-report-summary">
      <div><small>En çok kazandıran</small><strong data-testid="company-top-earner">{top ? <>{top.company !== NONE && <CompanyLogo name={top.company} size={18} />} {top.company}</> : "—"}</strong><em>{top ? money(top.personal) : ""}</em></div>
      <div><small>Toplam prim</small><strong data-testid="company-total-prim">{money(totals.prim)}</strong><em>{totals.count} poliçe</em></div>
      <div><small>Toplam komisyon</small><strong data-testid="company-total-commission">{money(totals.personal)}</strong><em>Ortalama %{totals.prim ? ((totals.personal / totals.prim) * 100).toFixed(1) : "0"}</em></div>
    </div>
  );
}

export function CompanyReport({ policies, customers, branches, settings, years }) {
  const [year, setYear] = useState(years[0]);
  const [sortBy, setSortBy] = useState("personal");
  const [selected, setSelected] = useState(null);
  const stats = useCompanyStats(policies, customers, branches, settings, year);
  const rows = useMemo(() => [...stats].sort((a, b) => b[sortBy] - a[sortBy]), [stats, sortBy]);
  const totals = rows.reduce((t, r) => ({ prim: t.prim + r.prim, personal: t.personal + r.personal, count: t.count + r.count }), { prim: 0, personal: 0, count: 0 });
  const total = totals[sortBy] || 1;
  const fmt = (v) => (sortBy === "count" ? v : money(v));
  return (
    <section className="panel table-panel" data-testid="company-report-panel">
      <div className="panel-head">
        <div><h2>Şirket bazında üretim</h2><p>Hangi sigorta şirketi size daha çok kazandırıyor? · {year} · Detay için satıra tıklayın</p></div>
        <div className="company-report-controls">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="company-report-year">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <div className="segmented" data-testid="company-report-sort">
            {SORTS.map(([k, l]) => <button key={k} type="button" className={sortBy === k ? "active" : ""} onClick={() => setSortBy(k)} data-testid={`company-sort-${k}`}>{l}</button>)}
          </div>
        </div>
      </div>
      {rows.length === 0 ? <Empty text="Bu yıl için şirket verisi yok" /> : (
        <div className="company-report">
          <Summary rows={rows} totals={totals} />
          <div className="company-report-chart" data-testid="company-report-chart">
            <ResponsiveContainer width="100%" height={Math.max(200, Math.min(rows.length, 12) * 38 + 40)}>
              <BarChart data={rows.slice(0, 12)} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid horizontal={false} stroke="#e8edf3" />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} tickFormatter={(v) => (sortBy === "count" ? v : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}K` : v)} />
                <YAxis dataKey="company" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#0f172a" }} width={130} />
                <Tooltip formatter={(v, n) => (n === "Poliçe" ? v : money(v))} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {sortBy === "count"
                  ? <Bar dataKey="count" name="Poliçe" fill="#1a5fe0" radius={[0, 4, 4, 0]} />
                  : <><Bar dataKey="prim" name="Brüt prim" fill="var(--navy)" radius={[0, 4, 4, 0]} /><Bar dataKey="personal" name="Komisyon" fill="#1a5fe0" radius={[0, 4, 4, 0]} /></>}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="table-scroll">
            <table data-testid="company-report-table">
              <thead><tr><th>#</th><th>ŞİRKET</th><th>POLİÇE</th><th>BRÜT PRİM</th><th>KOMİSYON</th><th>KOM. ORANI</th><th>ACENTE PRİMİ</th><th>PAY</th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.company} data-testid={`company-row-${r.company}`} className={`company-click${i < 3 ? " company-top" : ""}`} onClick={() => setSelected(r.company)} title="Detay için tıklayın">
                    <td><span className={`company-rank rank-${i + 1}`}>{i < 3 ? <Trophy size={11} /> : null}{i + 1}</span></td>
                    <td><span className="company-name-cell"><CompanyLogo name={r.company === NONE ? "" : r.company} size={22} /><strong>{r.company}</strong></span></td>
                    <td>{r.count}</td>
                    <td>{money(r.prim)}</td>
                    <td className="commission"><strong>{money(r.personal)}</strong></td>
                    <td>%{r.prim ? ((r.personal / r.prim) * 100).toFixed(1) : "0"}</td>
                    <td>{r.agent > 0 ? money(r.agent) : <em className="dim">—</em>}</td>
                    <td>
                      <div className="company-share" title={`${fmt(r[sortBy])}`}>
                        <div className="company-share-bar"><div style={{ width: `${(r[sortBy] / total) * 100}%` }} /></div>
                        <span>%{((r[sortBy] / total) * 100).toFixed(1)}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <CompanyDetailSheet company={selected} onClose={() => setSelected(null)} policies={policies} branches={branches} settings={settings} year={year} />
    </section>
  );
}
